/**
 * AppNavigation Component
 * Draggable navigation menu bar
 */

import React from 'react';
import { GripVertical, MoreHorizontal } from 'lucide-react';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import type { MenuItem } from '@/shared/types';
import { useSettings } from '@/features/settings/contexts/SettingsContext';

interface AppNavigationProps {
  theme: ThemeClasses;
  menuItems: MenuItem[];
  activeView: string;
  draggedMenu: { menu: MenuItem; index: number } | null;
  dragOverMenu: number | null;
  setActiveView: (view: string) => void;
  setDragOverMenu: (index: number | null) => void;
  handleMenuDragStart: (e: React.DragEvent, menu: MenuItem, index: number) => void;
  handleMenuDragOver: (e: React.DragEvent, index: number) => void;
  handleMenuDrop: (e: React.DragEvent, index: number) => void;
  handleMenuDragEnd: () => void;
  moveMenuItem?: (index: number, offset: -1 | 1) => void;
  isIphoneTouch?: boolean;
}

const MOBILE_PRIMARY_TABS: Array<{
  id: 'dashboard' | 'work' | 'docs' | 'activity';
  view: string;
}> = [
  { id: 'dashboard', view: 'dashboard' },
  { id: 'work', view: 'gantt' },
  { id: 'docs', view: 'docs' },
  { id: 'activity', view: 'reports' },
];

export const AppNavigation: React.FC<AppNavigationProps> = ({
  theme,
  menuItems,
  activeView,
  draggedMenu,
  dragOverMenu,
  setActiveView,
  setDragOverMenu,
  handleMenuDragStart,
  handleMenuDragOver,
  handleMenuDrop,
  handleMenuDragEnd,
  moveMenuItem,
  isIphoneTouch = false,
}) => {
  const { t } = useSettings();
  const [isMoreOpen, setIsMoreOpen] = React.useState(false);

  React.useEffect(() => {
    setIsMoreOpen(false);
  }, [activeView]);

  if (isIphoneTouch) {
    const primaryTabs = MOBILE_PRIMARY_TABS
      .map((tab) => {
        const menuItem = menuItems.find((item) => item.view === tab.view);
        return menuItem ? { ...tab, menuItem } : null;
      })
      .filter((tab): tab is typeof MOBILE_PRIMARY_TABS[number] & { menuItem: MenuItem } => Boolean(tab));
    const primaryViews = new Set(primaryTabs.map((tab) => tab.view));
    const moreItems = menuItems.filter((item) => !primaryViews.has(item.view));
    const isMoreActive = !primaryViews.has(activeView);

    return (
      <nav
        data-testid="app-mobile-nav"
        className={cn(
          'relative border-t px-2 pt-1.5',
          'pb-[calc(0.375rem+env(safe-area-inset-bottom))]',
          theme.bgSecondary,
          theme.borderPrimary,
        )}
        aria-label={t('Primary mobile navigation')}
      >
        {isMoreOpen && moreItems.length > 0 ? (
          <div
            data-testid="app-mobile-more-menu"
            className={cn(
              'absolute bottom-full left-2 right-2 mb-2 rounded-lg border p-1 shadow-2xl',
              theme.bgPrimary,
              theme.borderPrimary,
            )}
          >
            {moreItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`app-mobile-more-${item.view}`}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-left text-sm',
                    activeView === item.view
                      ? cn(theme.controlActive, theme.selectionRing)
                      : cn(theme.textSecondary, theme.surfaceHover),
                  )}
                  onClick={() => {
                    setIsMoreOpen(false);
                    setActiveView(item.view);
                  }}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{t(item.label)}</span>
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="grid grid-cols-5 gap-1">
          {primaryTabs.map((tab) => {
            const Icon = tab.menuItem.icon;
            const isActive = activeView === tab.view;
            return (
              <button
                key={tab.id}
                type="button"
                data-testid={`app-mobile-tab-${tab.id}`}
                className={cn(
                  'flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-md px-1 text-[11px] font-medium',
                  isActive
                    ? cn(theme.controlActive, theme.selectionRing)
                    : cn(theme.textSecondary, theme.surfaceHover),
                )}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => {
                  setIsMoreOpen(false);
                  setActiveView(tab.view);
                }}
              >
                <Icon className="h-4 w-4" />
                <span className="max-w-full truncate">{t(tab.menuItem.label)}</span>
              </button>
            );
          })}
          <button
            type="button"
            data-testid="app-mobile-tab-more"
            className={cn(
              'flex min-h-12 flex-col items-center justify-center gap-0.5 rounded-md px-1 text-[11px] font-medium',
              isMoreActive || isMoreOpen
                ? cn(theme.controlActive, theme.selectionRing)
                : cn(theme.textSecondary, theme.surfaceHover),
            )}
            aria-expanded={isMoreOpen}
            aria-current={isMoreActive ? 'page' : undefined}
            onClick={() => setIsMoreOpen((current) => !current)}
          >
            <MoreHorizontal className="h-4 w-4" />
            <span className="max-w-full truncate">{t('More')}</span>
          </button>
        </div>
      </nav>
    );
  }

  return (
    <nav
      className={cn(
        'pm-app-navigation min-h-9 min-w-0 shrink-0 border-b flex items-center',
        theme.bgSecondary,
        theme.borderPrimary
      )}
    >
      <div className="flex min-w-0 w-full flex-wrap items-center gap-1 px-2 py-1">
        <span id="app-nav-reorder-hint" className="sr-only">
          {t('Press Alt+Shift+Left or Right Arrow to move this tab.')}
        </span>
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              key={item.id}
              draggable
              data-tour-target={`nav-${item.view}`}
              onDragStart={(e) => handleMenuDragStart(e, item, index)}
              onDragOver={(e) => handleMenuDragOver(e, index)}
              onDrop={(e) => handleMenuDrop(e, index)}
              onDragEnd={handleMenuDragEnd}
              onDragEnter={(e) => {
                e.preventDefault();
                if (draggedMenu && draggedMenu.index !== index) {
                  setDragOverMenu(index);
                }
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragOverMenu(null);
              }}
              className={cn(
                'flex shrink-0 items-center gap-1 rounded px-1.5 py-1 transition-all cursor-move whitespace-nowrap lg:px-2',
                activeView === item.view
                  ? cn(theme.controlActive, theme.selectionRing)
                  : cn(theme.textSecondary, theme.surfaceHover),
                dragOverMenu === index
                  ? 'border-l-2 border-zinc-400/70 dark:border-white/20'
                  : '',
                draggedMenu?.index === index ? 'opacity-50' : ''
              )}
            >
              <GripVertical className="hidden h-3 w-3 shrink-0 opacity-50 2xl:block" aria-hidden="true" />
              <button
                type="button"
                onClick={() => setActiveView(item.view)}
                onKeyDown={(event) => {
                  if (!moveMenuItem || !event.altKey || !event.shiftKey) return;
                  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                  event.preventDefault();
                  moveMenuItem(index, event.key === 'ArrowLeft' ? -1 : 1);
                }}
                aria-keyshortcuts={moveMenuItem ? 'Alt+Shift+ArrowLeft Alt+Shift+ArrowRight' : undefined}
                aria-describedby={moveMenuItem ? 'app-nav-reorder-hint' : undefined}
                data-testid={`app-nav-${item.view}`}
                aria-label={t(item.label)}
                aria-current={activeView === item.view ? 'page' : undefined}
                title={t(item.label)}
                className="flex min-w-0 shrink items-center gap-1 whitespace-nowrap"
              >
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap text-sm">{t(item.label)}</span>
              </button>
            </div>
          );
        })}
      </div>
    </nav>
  );
};
